import { useLoginUser } from "@hooks/useAuth";
import type * as type from "@vTypes";
import { forwardRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "@/components/toast/CustomToast";
import LoadingSpiner from "@components/ui/LoadingSpiner";
import { usePersonnelBasicInfo } from "@/hooks/usePersonnelBasicInfo";

// 문의하기 폼 스키마
const inquiryFormSchema = z.object({
  title: z.string().min(1, "문의 제목을 입력해주세요"),
  contents: z.string().min(1, "문의 내용을 입력해주세요"),
});

type InquiryFormValues = z.infer<typeof inquiryFormSchema>;

// 읽기 전용 정보 표시 컴포넌트
function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) {
  return (
    <div className="flex items-center gap-4">
      <div className="text-sm font-medium min-w-[80px]">{label}</div>
      <div className="text-sm text-gray-700">{value || "-"}</div>
    </div>
  );
}

// 커스텀 Input 컴포넌트
const CustomInput = forwardRef<
  HTMLInputElement,
  {
    label: string;
    required?: boolean;
    className?: string;
    error?: string;
  } & React.InputHTMLAttributes<HTMLInputElement>
>(({ label, required = false, className = "", error, ...props }, ref) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-4">
        <div className="text-sm font-medium min-w-[80px]">
          {label} {required && <span className="textAccent_warn">*</span>}
        </div>
        <input
          ref={ref}
          className={`cs_inputText flex-1 ${className} ${
            error ? "border-red-500" : ""
          }`}
          required={required}
          {...props}
        />
      </div>
      {error && <div className="text-red-500 text-xs ml-[96px]">{error}</div>}
    </div>
  );
});

CustomInput.displayName = "CustomInput";

// 커스텀 Textarea 컴포넌트
const CustomTextarea = forwardRef<
  HTMLTextAreaElement,
  {
    label: string;
    required?: boolean;
    className?: string;
    rows?: number;
    error?: string;
  } & React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(
  (
    { label, required = false, className = "", rows = 8, error, ...props },
    ref
  ) => {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-4">
          <div className="text-sm font-medium min-w-[80px] pt-2">
            {label} {required && <span className="textAccent_warn">*</span>}
          </div>
          <textarea
            ref={ref}
            className={`cs_inputText flex-1 ${className} ${
              error ? "border-red-500" : ""
            }`}
            rows={rows}
            required={required}
            {...props}
          />
        </div>
        {error && <div className="text-red-500 text-xs ml-[96px]">{error}</div>}
      </div>
    );
  }
);

CustomTextarea.displayName = "CustomTextarea";

export default function Customer_write({
  writeQuestion,
  onCancel,
}: {
  writeQuestion: (payload: type.QuestionPayload) => void;
  onCancel?: () => void;
}) {
  const loginUser = useLoginUser();
  const { data: userDetailData } = usePersonnelBasicInfo();

  const [isPending, setIsPending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InquiryFormValues>({
    resolver: zodResolver(inquiryFormSchema),
    defaultValues: {
      title: "",
      contents: "",
    },
  });

  async function onSubmit(data: InquiryFormValues) {
    if (loginUser) {
      setIsPending(true);
      try {
        const questionPayload: type.QuestionPayload = {
          username: loginUser.name || "",
          PortableTel: userDetailData?.phone_number || "",
          Email: userDetailData?.email || "",
          title: data.title,
          contents: data.contents,
          files: [],
        };
        await writeQuestion(questionPayload);
        toast.success("문의가 등록되었습니다.", {
          duration: 3000,
        });
        reset();
      } catch (error) {
        toast.error("문의 등록 중 오류가 발생했습니다.");
        console.error("문의 등록 오류:", error);
      } finally {
        setIsPending(false);
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="">
        <div className="cs_pageTitle">문의하기</div>

        {isPending ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpiner msg="문의를 등록하는 중입니다..." />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 pb-5 border-b border-zinc-200 mb-5">
              <ReadOnlyField label="이름" value={loginUser?.name} />
              <ReadOnlyField
                label="연락처"
                value={userDetailData?.phone_number}
              />
              <ReadOnlyField label="이메일" value={userDetailData?.email} />
            </div>

            <div className="cs_write_sec cs_sec_writeQuestion">
              <CustomInput
                label="문의 제목"
                required
                className="w-600px placeholder:text-sm text-base"
                error={errors.title?.message}
                {...register("title")}
                placeholder="문의 제목을 입력해주세요"
                disabled={isPending}
              />

              <CustomTextarea
                label="문의 내용"
                required
                className="w-600px placeholder:text-sm text-base"
                rows={8}
                error={errors.contents?.message}
                {...register("contents")}
                placeholder="최소 10자 이상 입력해주세요"
                disabled={isPending}
              />
            </div>

            <div className="flex justify-center gap-2">
              <button
                type="button"
                className="border py-3 border-zinc-300 min-w-[150px] rounded-full hover:border-zinc-400 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onCancel}
                disabled={isPending}
              >
                취소
              </button>
              <button
                type="submit"
                className="border py-3 border-zinc-300 min-w-[150px] rounded-full hover:border-zinc-400 bg-sig text-white hover:bg-sig/80 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isPending}
              >
                {isPending ? "등록 중..." : "문의하기"}
              </button>
            </div>
          </>
        )}
      </div>
    </form>
  );
}
